import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Section, Select } from '../components/forms';
import { Button, ErrorNote, Field } from '../components/ui';
import { api, ApiError, type Me } from '../lib/api';
import { TwoFactorRequired } from '../components/AppLayout';
import { useTwoFactorGate } from '../components/TwoFactor';
import { useLocale, useT, type TKey } from '../lib/i18n';

type ErrorRow = { id: string; source: string; message: string; stack: string | null; path: string | null; count: number; lastSeen: string };
type Health = { salons: number; activeSalons7d: number; bookings24h: number; onlineBookings24h: number; messagesSent24h: number; messagesFailed24h: number; messagesOverdue: number; accountEmailsOverdue: number };
type Row = { id: string; name: string; slug: string; status: string; trialEndsAt: string | null; comped: boolean; expiredAt: string | null; subscription: { status: string; interval: 'month' | 'year'; quantity: number; pastDueSince: string | null; cancelAt: string | null } | null; ownerEmail: string | null; invitedOwner: string | null; appointments: number; onlineBookings: number; lastActivity: string | null; supportAccessUntil: string | null };
const types = ['hair', 'barber', 'nails', 'beauty', 'pedicure', 'massage', 'lashes_brows', 'other'] as const;

/** Back office (/beheer): our team only. The API decides who gets in; this page just shows it. */
export function AdminPage() {
  const t = useT();
  const locale = useLocale();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/me') });
  // The back office always needs the second step; don't even ask the API before it's on.
  const ready = !!me.data?.user.twoFactorEnabled;
  const gate = useTwoFactorGate(!!me.data && !ready);
  const health = useQuery({ queryKey: ['admin', 'health'], queryFn: () => api<Health>('/admin/health'), refetchInterval: 60_000, enabled: ready });
  const salons = useQuery({ queryKey: ['admin', 'salons', q], queryFn: () => api<{ salons: Row[] }>(`/admin/salons?q=${encodeURIComponent(q)}`), placeholderData: (prev) => prev, enabled: ready });
  const errors = useQuery({ queryKey: ['admin', 'errors'], queryFn: () => api<{ errors: ErrorRow[] }>('/admin/errors'), refetchInterval: 60_000, enabled: ready });
  const solve = useMutation({ mutationFn: (id: string) => api(`/admin/errors/${id}/resolve`, { method: 'POST' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'errors'] }) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin'] });
  const extend = useMutation({ mutationFn: (id: string) => api(`/admin/salons/${id}/trial`, { method: 'POST', body: JSON.stringify({ days: 7 }) }), onSuccess: refresh });
  const comp = useMutation({ mutationFn: ({ id, comped }: { id: string; comped: boolean }) => api(`/admin/salons/${id}/comped`, { method: 'POST', body: JSON.stringify({ comped }) }), onSuccess: refresh });
  const open = useMutation({
    mutationFn: (id: string) => api(`/admin/salons/${id}/support`, { method: 'POST' }).then(() => id),
    onSuccess: (id) => {
      qc.removeQueries({ queryKey: ['me'] });
      qc.removeQueries({ queryKey: ['salon', id] });
      navigate({ to: '/s/$orgId/vandaag', params: { orgId: id } });
    },
  });
  const tag = locale === 'nl' ? 'nl-NL' : 'en-GB';
  const date = (iso: string) => new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
  const err = (e: unknown) => (e instanceof ApiError ? e.message : e ? t('error.generic') : null);
  const h = health.data;
  if (gate.show) {
    return (
      <div className="min-h-dvh px-4 py-6 md:px-8">
        <TwoFactorRequired message={t('tf.requiredAdmin')} onActive={gate.onActive} />
      </div>
    );
  }
  const tiles: Array<[TKey, number | undefined, boolean?]> = [
    ['admin.h.salons', h?.salons],
    ['admin.h.active', h?.activeSalons7d],
    ['admin.h.bookings', h?.bookings24h],
    ['admin.h.online', h?.onlineBookings24h],
    ['admin.h.sent', h?.messagesSent24h],
    ['admin.h.failed', h?.messagesFailed24h, (h?.messagesFailed24h ?? 0) > 0],
    ['admin.h.overdue', h ? h.messagesOverdue + h.accountEmailsOverdue : undefined, (h?.messagesOverdue ?? 0) + (h?.accountEmailsOverdue ?? 0) > 0],
    ['admin.h.errors', errors.data?.errors.length, (errors.data?.errors.length ?? 0) > 0],
  ];

  // What needs a person: trials about to end or ended, open support access, salons nobody owns yet.
  const soon = Date.now() + 3 * 86_400_000;
  const attention = (salons.data?.salons ?? []).flatMap((s) => {
    const notes: Array<{ id: string; name: string; text: string; tone: 'warning' | 'danger' | 'muted' }> = [];
    if (s.status === 'expired') notes.push({ id: s.id, name: s.name, text: s.expiredAt ? t('admin.att.deletes', { date: date(new Date(Date.parse(s.expiredAt) + 90 * 86_400_000).toISOString()) }) : t('admin.att.expired'), tone: 'danger' });
    else if (s.status === 'past_due' && s.subscription?.pastDueSince) notes.push({ id: s.id, name: s.name, text: t('admin.att.pastDue', { date: date(s.subscription.pastDueSince) }), tone: 'warning' });
    else if (s.status === 'suspended') notes.push({ id: s.id, name: s.name, text: t('admin.att.suspended'), tone: 'danger' });
    else if (s.status === 'trialing' && s.trialEndsAt && Date.parse(s.trialEndsAt) < soon) notes.push({ id: s.id, name: s.name, text: t('admin.att.trialEnds', { date: date(s.trialEndsAt) }), tone: 'warning' });
    if (s.supportAccessUntil && Date.parse(s.supportAccessUntil) > Date.now()) notes.push({ id: s.id, name: s.name, text: t('admin.att.support', { date: date(s.supportAccessUntil) }), tone: 'muted' });
    if (!s.ownerEmail) notes.push({ id: s.id, name: s.name, text: t('admin.att.noOwner'), tone: 'muted' });
    return notes;
  });
  const statusTag = (s: Row) =>
    s.status === 'active' ? 'bg-success-soft text-success' : s.status === 'trialing' ? 'bg-accent-soft text-accent-strong' : s.status === 'expired' || s.status === 'past_due' ? 'bg-danger-soft text-danger' : 'bg-subtle text-muted';

  return (
    <div className="min-h-dvh px-4 py-6 md:px-8 md:py-10">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-8">
        <header className="relative flex flex-wrap items-end justify-between gap-4">
          <div className="relative">
            <p className="eyebrow">{t('admin.eyebrow')}</p>
            <h1 className="page-title mt-2">{t('admin.title')}</h1>
            <p className="mt-2 text-muted">{t('admin.intro')}</p>
          </div>
          <a href="/" className="relative text-sm font-semibold text-accent-strong hover:underline">
            ← {t('admin.back')}
          </a>
        </header>

        {/* Health in numbers, like the totals at the foot of a page. */}
        <section aria-label={t('admin.health')} className="grid grid-cols-2 gap-px overflow-hidden rounded-[var(--radius-card)] border border-line bg-line sm:grid-cols-4 xl:grid-cols-8">
          {tiles.map(([k, v, bad]) => (
            <div key={k} className={`flex flex-col px-5 py-4 ${bad ? 'bg-danger-soft text-danger' : 'bg-surface'}`}>
              <span className="eyebrow !text-current opacity-80">{t(k)}</span>
              <span className="display mt-2 text-[38px] leading-none tabular-nums">{v ?? '…'}</span>
            </div>
          ))}
        </section>
        {(h?.messagesOverdue ?? 0) + (h?.accountEmailsOverdue ?? 0) > 0 && <p className="-mt-4 text-sm text-danger">{t('admin.overdueHint')}</p>}

        <div className="flex flex-col gap-6 xl:grid xl:grid-cols-[minmax(0,1fr)_400px] xl:items-start">
          <section className="flex min-w-0 flex-col gap-3">
            <input
              type="search"
              aria-label={t('admin.search')}
              placeholder={t('admin.search')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="min-h-12 w-full max-w-md rounded-full border border-line bg-surface px-5 outline-none focus:border-accent-strong"
            />
            <ErrorNote message={err(extend.error) ?? err(open.error)} />
            <div className="overflow-x-auto rounded-[var(--radius-card)] border border-line bg-surface shadow-[0_1px_2px_rgb(29_25_21/0.04)]">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="border-b border-line">
                  <tr>
                    {(['admin.col.salon', 'admin.col.owner', 'admin.col.status', 'admin.col.bookings', 'admin.col.activity'] as const).map((k) => (
                      <th key={k} className="eyebrow px-4 py-2.5 font-medium">
                        {t(k)}
                      </th>
                    ))}
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {salons.data?.salons.map((s) => {
                    const consent = !!s.supportAccessUntil && new Date(s.supportAccessUntil) > new Date();
                    return (
                      <tr key={s.id} className="border-b border-line last:border-0 hover:bg-subtle/60">
                        <td className="min-w-[190px] px-4 py-3">
                          <p className="font-semibold">{s.name}</p>
                          <p className="text-muted">/{s.slug}</p>
                        </td>
                        <td className="px-4 py-3">{s.ownerEmail ?? <span className="text-muted">{s.invitedOwner ? t('admin.invited', { email: s.invitedOwner }) : t('admin.noOwner')}</span>}</td>
                        <td className="whitespace-nowrap px-4 py-3">
                          {s.comped ? (
                            <span className="tag bg-success-soft text-success">{t('admin.status.comped')}</span>
                          ) : (
                            <span className={`tag ${statusTag(s)}`}>
                              {s.status === 'trialing' && s.trialEndsAt
                                ? t('admin.status.trialing', { date: date(s.trialEndsAt) })
                                : s.status === 'active' && s.subscription
                                  ? t('admin.status.paying', { n: s.subscription.quantity, interval: t(`sub.interval.${s.subscription.interval}` as TKey) })
                                  : t(`admin.status.${s.status}` as TKey)}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 tabular-nums">
                          {s.appointments} <span className="text-muted">({s.onlineBookings} online)</span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-muted">{s.lastActivity ? date(s.lastActivity) : '–'}</td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" className="min-h-9 px-3 text-sm" onClick={() => comp.mutate({ id: s.id, comped: !s.comped })} disabled={comp.isPending} title={t(s.comped ? 'admin.comped.offHint' : 'admin.comped.onHint')}>
                              {t(s.comped ? 'admin.comped.off' : 'admin.comped.on')}
                            </Button>
                            {!s.comped && (s.status === 'trialing' || s.status === 'expired') && (
                              <Button variant="ghost" className="min-h-9 px-3 text-sm" onClick={() => extend.mutate(s.id)} disabled={extend.isPending}>
                                {t('admin.extend')}
                              </Button>
                            )}
                            <Button variant="secondary" className="min-h-9 px-3 text-sm" onClick={() => open.mutate(s.id)} disabled={!consent || open.isPending} title={consent ? undefined : t('admin.noConsent')}>
                              {consent ? t('admin.open') : t('admin.noConsent')}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {salons.data?.salons.length === 0 && <p className="px-4 py-6 text-muted">{t('admin.none')}</p>}
            </div>
          </section>

          <aside className="flex flex-col gap-4 xl:sticky xl:top-6">
            {ready && <BillingAttention />}
            <section className="rounded-[var(--radius-card)] border border-line bg-surface p-5 shadow-[0_1px_2px_rgb(29_25_21/0.04)]">
              <div className="flex items-baseline justify-between">
                <h2 className="display text-[24px]">{t('admin.attention')}</h2>
                {attention.length > 0 && <span className="tag bg-warning-soft text-warning">{attention.length}</span>}
              </div>
              {attention.length === 0 ? (
                <p className="mt-2 text-[14px] text-muted">{t('admin.att.none')}</p>
              ) : (
                <ul className="mt-3 flex flex-col">
                  {attention.slice(0, 8).map((n, i) => (
                    <li key={`${n.id}:${i}`} className="flex items-start gap-3 border-t border-line py-2.5 first:border-t-0">
                      <span aria-hidden className={`mt-1.5 size-2 shrink-0 rounded-full ${n.tone === 'danger' ? 'bg-danger' : n.tone === 'warning' ? 'bg-warning' : 'bg-line'}`} />
                      <span className="min-w-0">
                        <span className="block truncate text-[15px] font-medium">{n.name}</span>
                        <span className="block text-[13px] text-muted">{n.text}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className="rounded-[var(--radius-card)] border border-line bg-surface p-5 shadow-[0_1px_2px_rgb(29_25_21/0.04)]">
              <div className="flex items-baseline justify-between">
                <h2 className="display text-[24px]">{t('admin.errors')}</h2>
                {(errors.data?.errors.length ?? 0) > 0 && <span className="tag bg-danger-soft text-danger">{errors.data!.errors.length}</span>}
              </div>
              {!errors.data?.errors.length ? (
                <p className="mt-2 text-[14px] text-muted">{t('admin.errors.none')}</p>
              ) : (
                <ul className="mt-3 flex flex-col">
                  {errors.data.errors.slice(0, 10).map((e) => (
                    <li key={e.id} className="flex flex-col gap-1 border-t border-line py-2.5 first:border-t-0">
                      <div className="flex items-start justify-between gap-3">
                        <span className="min-w-0">
                          <span className="tag mr-2 bg-subtle text-muted">{e.source}</span>
                          <span className="break-words text-[14px] font-medium">{e.message}</span>
                        </span>
                        <button type="button" onClick={() => solve.mutate(e.id)} disabled={solve.isPending} className="shrink-0 text-[13px] font-semibold text-accent-strong hover:underline">
                          {t('admin.errors.resolve')}
                        </button>
                      </div>
                      <span className="text-[12px] text-muted">
                        {t('admin.errors.seen', { n: e.count, date: date(e.lastSeen) })}
                        {e.path && ` · ${e.path}`}
                      </span>
                      {e.stack && (
                        <details className="text-[12px]">
                          <summary className="cursor-pointer text-muted">Stack</summary>
                          <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded-[8px] bg-subtle p-2 font-mono text-[11px] leading-snug">{e.stack}</pre>
                        </details>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <CreateSalon onCreated={refresh} />
          </aside>
        </div>
      </div>
    </div>
  );
}

function CreateSalon({ onCreated }: { onCreated: () => void }) {
  const t = useT();
  const [type, setType] = useState<(typeof types)[number]>('hair');
  const [done, setDone] = useState(false);
  const create = useMutation({
    mutationFn: (body: Record<string, string>) => api('/admin/salons', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      setDone(true);
      onCreated();
    },
  });
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setDone(false);
    create.mutate({ name: String(f.get('name')), businessType: type, ownerName: String(f.get('ownerName')), ownerEmail: String(f.get('ownerEmail')) }, { onSuccess: () => (e.target as HTMLFormElement).reset() });
  }
  return (
    <Section title={t('admin.create')} intro={t('admin.createIntro')}>
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
        <Field id="name" name="name" label={t('admin.createSalon')} required minLength={2} maxLength={80} />
        <Select id="businessType" label={t('admin.createType')} value={type} onChange={(e) => setType(e.target.value as (typeof types)[number])}>
          {types.map((v) => (
            <option key={v} value={v}>
              {t(`type.${v}`)}
            </option>
          ))}
        </Select>
        <Field id="ownerName" name="ownerName" label={t('admin.createOwner')} required maxLength={60} />
        <Field id="ownerEmail" name="ownerEmail" type="email" label={t('admin.createEmail')} required />
        <div className="flex flex-wrap items-center gap-3 sm:col-span-2 xl:col-span-1">
          <Button type="submit" disabled={create.isPending}>
            {t('admin.createSubmit')}
          </Button>
          {done && <p className="text-sm font-medium text-accent-strong">✓ {t('admin.created')}</p>}
        </div>
        <div className="sm:col-span-2 xl:col-span-1">
          <ErrorNote message={create.error instanceof ApiError ? create.error.message : create.error ? t('error.generic') : null} />
        </div>
      </form>
    </Section>
  );
}

type BillingItem = { id: string; type: 'unmatched' | 'duplicate'; objectId: string | null; payload: { email?: string | null; status?: string; quantity?: number }; receivedAt: string };

/** Payments that need a person: a subscription no salon matched, or a salon that pays twice. */
function BillingAttention() {
  const t = useT();
  const qc = useQueryClient();
  const items = useQuery({ queryKey: ['admin', 'billing'], queryFn: () => api<{ attention: BillingItem[] }>('/admin/billing'), refetchInterval: 60_000 });
  const done = useMutation({ mutationFn: (id: string) => api(`/admin/billing/${id}/dismiss`, { method: 'POST' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'billing'] }) });
  const list = items.data?.attention ?? [];
  if (list.length === 0) return null;
  return (
    <section className="rounded-[var(--radius-card)] border border-warning/40 bg-warning-soft p-5">
      <h2 className="display text-[24px]">{t('admin.billing.title')}</h2>
      <p className="mt-1 text-[14px] text-warning">{t('admin.billing.intro')}</p>
      <ul className="mt-3 flex flex-col">
        {list.map((b) => (
          <li key={b.id} className="flex items-start justify-between gap-3 border-t border-warning/20 py-2.5 first:border-t-0">
            <span className="min-w-0 text-[14px]">
              <span className="block font-medium">{t(b.type === 'duplicate' ? 'admin.billing.duplicate' : 'admin.billing.unmatched')}</span>
              <span className="block truncate text-muted">
                {b.payload.email ?? '–'} · Plug&Pay #{b.objectId}
              </span>
            </span>
            <button type="button" className="shrink-0 text-sm font-semibold text-accent-strong hover:underline" onClick={() => done.mutate(b.id)} disabled={done.isPending}>
              {t('admin.billing.done')}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
